// P&L journalier — compte de résultat quotidien
import { useState, useMemo } from "react";
import { Calculator, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useDailyPnL } from "@/hooks/admin/useFinance";
import type { DailyPnL } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getLast30DaysRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const dateTo = now.toISOString().split("T")[0];
  const from = new Date(now);
  from.setDate(from.getDate() - 29);
  const dateFrom = from.toISOString().split("T")[0];
  return { dateFrom, dateTo };
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number | null | undefined): string {
  const n = Number(amount);
  if (isNaN(n)) return "—";
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Table row component
// ---------------------------------------------------------------------------

interface PnLRowProps {
  readonly entry: DailyPnL;
  readonly isTotals?: boolean;
}

function PnLRow({ entry, isTotals }: PnLRowProps) {
  const rowClass = isTotals
    ? "bg-stone-50 font-semibold border-t-2 border-stone-300"
    : "hover:bg-stone-50";

  return (
    <tr className={rowClass}>
      <td className="px-4 py-3 text-sm text-stone-900">
        {isTotals ? (
          <span className="font-semibold text-stone-700">Totaux</span>
        ) : (
          formatDate(entry.date)
        )}
      </td>
      <td className="px-4 py-3 text-sm text-stone-900">
        {formatFcfa(entry.revenue_fcfa)}
      </td>
      <td className="px-4 py-3 text-sm text-stone-600">
        {formatFcfa(entry.cogs_fcfa)}
      </td>
      <td className="px-4 py-3 text-sm font-medium text-stone-900">
        {formatFcfa(entry.gross_profit_fcfa)}
      </td>
      <td className="px-4 py-3 text-sm">
        <span
          className={
            entry.gross_margin_percent >= 30
              ? "font-semibold text-emerald-700"
              : entry.gross_margin_percent >= 15
                ? "font-semibold text-amber-700"
                : "font-semibold text-red-700"
          }
        >
          {(entry.gross_margin_percent ?? 0).toFixed(1)}%
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-stone-600">
        {formatFcfa(entry.operating_expenses_fcfa)}
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center gap-1">
          {entry.ebitda_fcfa >= 0 ? (
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5 text-red-600" />
          )}
          <span
            className={`font-semibold ${
              entry.ebitda_fcfa >= 0 ? "text-emerald-700" : "text-red-700"
            }`}
          >
            {formatFcfa(entry.ebitda_fcfa)}
          </span>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DailyPnL() {
  const defaults = useMemo(() => getLast30DaysRange(), []);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);

  const { data, isLoading, isError, refetch } = useDailyPnL(dateFrom, dateTo);

  const entries = data?.entries ?? [];
  const totals = data?.totals;

  const TABLE_HEADERS = [
    "Date",
    "CA (FCFA)",
    "COGS (FCFA)",
    "Marge brute",
    "Marge %",
    "Charges OpEx",
    "EBITDA",
  ] as const;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Calculator className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                P&L journalier
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading ? "Chargement…" : `${entries.length} jour(s)`}
              </p>
            </div>
          </div>

          {/* Date range */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-stone-600">Du :</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-stone-600">Au :</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="CA total"
            value={totals?.revenue_fcfa ?? 0}
            format="currency"
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <KPICard
            label="Marge brute"
            value={totals?.gross_profit_fcfa ?? 0}
            format="currency"
            icon={<Calculator className="h-4 w-4" />}
          />
          <KPICard
            label="Marge brute %"
            value={totals?.gross_margin_percent ?? 0}
            format="percent"
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <KPICard
            label="EBITDA"
            value={totals?.ebitda_fcfa ?? 0}
            format="currency"
            icon={
              (totals?.ebitda_fcfa ?? 0) >= 0 ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )
            }
          />
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger le P&L journalier.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && entries.length === 0 ? (
              <EmptyState
                icon={<Calculator className="h-6 w-6" />}
                title="Aucune donnée P&L"
                description="Sélectionnez une plage de dates pour afficher le P&L."
              />
            ) : (
              <div className="overflow-x-auto">
                <div className="overflow-hidden rounded-lg border border-stone-200">
                  <table className="w-full">
                    <thead className="bg-stone-50">
                      <tr>
                        {TABLE_HEADERS.map((h) => (
                          <th
                            key={h}
                            className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 bg-white">
                      {isLoading
                        ? Array.from({ length: 7 }).map((_, i) => (
                            <tr key={i}>
                              {Array.from({ length: 7 }).map((__, j) => (
                                <td key={j} className="px-4 py-3">
                                  <div className="h-4 animate-pulse rounded bg-stone-100" />
                                </td>
                              ))}
                            </tr>
                          ))
                        : entries.map((entry) => (
                            <PnLRow key={entry.date} entry={entry} />
                          ))}
                      {!isLoading && totals && (
                        <PnLRow entry={totals} isTotals />
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
